import React, { useEffect, useRef, useState } from "react";
import InfoIcon from "assets/images/icons/info.svg";
import CashIcon from "assets/images/icons/cash.svg";
import { useTranslation } from "react-i18next";
import { Minus, Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { showAlert } from "store/reducers/alertReducer";
import { cancelOrder, getOrderDetails } from "store/actions/orderSlice";
import { orderSpaceType } from "components/constants/DropdownOptions";
import { formatDate } from "components/common/Utils";
import { orderStatusTypes } from "components/constants/StatusKeys";
import CustomLabel from "components/common/CustomLabel";
import Riyal from "assets/images/icons/darkRiyal.svg";
import { SuccessIcon } from "components/common/SVGIcons";

const CancelOrder = ({ setShow, orderDetails }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { isLoading } = useSelector((state) => state.Orders);

  const [cancelStep, setCancelStep] = useState(1);
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState(
    orderDetails?.paidAmount === 0 &&
      orderDetails?.splitPayments?.walletAmount === 0
      ? 0
      : orderDetails?.splitPayments?.total
  );

  const [refundAmountType, setRefundAmountType] = useState(
    orderDetails?.paymentMethod === "FULL_DISCOUNT" ? 0 : 1
  );

  const handleRefundClick = () => {
    const payload = {
      bookingId: orderDetails?._id,
      reason,
      refundAmountType: refundAmountType, // 1 FOR Wallet/cashback, 0 for 100% discount, 2:original Payment Method, 3:Partial
      refundAmount: amount,
    };

    dispatch(cancelOrder(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          // dispatch(showAlert({ text: message, type: "success" }));
          setCancelStep(3);
          dispatch(getOrderDetails({ id: orderDetails?._id }));
          // HIDE AUTO MODAL AFTER 3s
          setTimeout(() => {
            setShow(false);
          }, 3000);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  // Reusable Function to handle Amount Change
  const handleAmountChange = (newAmount) => {
    // If paidAmount is 0, don't allow changes
    if (
      orderDetails?.paidAmount === 0 &&
      orderDetails?.splitPayments?.walletAmount === 0
    ) {
      return;
    }
    if (newAmount <= orderDetails?.splitPayments?.total) {
      setAmount(newAmount);
    } else {
      setAmount(orderDetails?.splitPayments?.total);
      dispatch(
        showAlert({
          text: `${t("Maximum allowed amount is")} ${
            orderDetails?.splitPayments?.total
          }`,
          type: "error",
        })
      );
    }
  };

  // Reusable Button Component
  const AmountButton = ({ action, icon, disabled }) => (
    <button
      className={
        action === "increase" ? "bg_primary text-white" : "bg_danger text-white"
      }
      onClick={
        action === "increase"
          ? () => handleAmountChange(amount + 1)
          : () => handleAmountChange(Math.max(amount - 1, 0))
      }
      disabled={
        (orderDetails?.paidAmount === 0 &&
          orderDetails?.splitPayments?.walletAmount === 0) ||
        (action === "increase" &&
          amount >= orderDetails?.splitPayments?.total) ||
        (action === "decrease" && amount <= 0) ||
        refundAmountType === 3
      }
    >
      {icon}
    </button>
  );

  return (
    <>
      {cancelStep === 1 ? (
        <div>
          <img
            className="d-block mx-auto"
            src={InfoIcon}
            width={70}
            height={70}
            alt=""
          />
          <h2 className="fs_20 text-center mt-3 text_black">
            {t("Are you sure you want to cancel")}
          </h2>
          <h2 className="fs_20 text-center text_black">
            ({t("Booking")} {orderDetails?.bookingNo})
          </h2>
          <CardDetailsCard orderDetails={orderDetails} t={t} />
          <div className="buttons mt-4">
            <button className="cancel_btn_2" onClick={() => setCancelStep(2)}>
              {t("Yes, Cancel")}
            </button>
            <button className="back_btn mt-3" onClick={() => setShow(false)}>
              {t("No, Go Back")}
            </button>
          </div>
        </div>
      ) : cancelStep === 2 ? (
        <div>
          <img
            className="d-block mx-auto"
            src={CashIcon}
            width={70}
            height={70}
            alt=""
          />
          <h2 className="fs_20 text-center mt-3 text_black">
            {t("Refund via Wallet Credit")}
          </h2>
          <p className="fs_14 text_gray text-center mt-3">
            {t(
              "The refund for this cancellation will be credited to the customer's wallet, Amount to be credited"
            )}
            :
            <img
              src={Riyal}
              width={15}
              height={15}
              alt="riyal"
              className="me-1"
            />
            {amount}
          </p>
          <div className="order_room_details refund_counter">
            <div className="g_center">
              {/* Reusable Amount Buttons */}
              <AmountButton
                action="increase"
                icon={<Plus />}
                disabled={amount >= orderDetails?.splitPayments?.total}
              />
              <input
                type="text"
                value={amount}
                onChange={(e) => handleAmountChange(Number(e.target.value))}
                disabled={refundAmountType === 3}
              />
              <AmountButton action="decrease" icon={<Minus />} />
            </div>
          </div>
          <CardDetailsCard orderDetails={orderDetails} t={t} />
          {/* <select name="" id="">
            <option value="">Wallet</option>
            <option value="">Original Payment method</option>
            <option value="">Partial Payment</option>
            <option value="">No refund</option> // amount 0 and amountType:0
          </select> */}
          <div className="form_field">
            <div className="mt-4"></div>
            <CustomLabel label={t("Refund Amount Type")} />
            <select
              className="form-control"
              value={refundAmountType}
              onChange={(e) => {
                const value = Number(e.target.value);
                setRefundAmountType(value);

                if (value === 3) {
                  setAmount(orderDetails?.splitPayments?.total);
                }
              }}
            >
              <option
                value={1}
                disabled={
                  !(
                    orderDetails?.splitPayments?.walletAmount > 0 ||
                    orderDetails?.splitPayments?.gatewayAmount > 0
                  )
                }
              >
                Wallet
              </option>

              <option
                value={0}
                disabled={orderDetails?.paymentMethod !== "FULL_DISCOUNT"}
              >
                100% Discount
              </option>
              <option
                value={2}
                disabled={
                  !(
                    orderDetails?.splitPayments?.gatewayAmount > 0 &&
                    orderDetails?.splitPayments?.walletAmount === 0
                  )
                }
              >
                Original Payment Method
              </option>
              <option
                value={3}
                disabled={
                  !(
                    orderDetails?.splitPayments?.gatewayAmount > 0 &&
                    orderDetails?.splitPayments?.walletAmount > 0
                  )
                }
              >
                Partial
              </option>
            </select>
          </div>
          <div className="form_field mt-3">
            <label htmlFor="reason">{t("Cancellation Reason")}</label>
            <textarea
              name="reason"
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("Enter Cancellation Reason")}
            />
          </div>
          <div className="buttons mt-4">
            <button
              className="cancel_btn_2 bg_primary"
              onClick={handleRefundClick}
              disabled={isLoading}
            >
              {orderDetails?.paidAmount === 0 &&
              orderDetails?.splitPayments?.walletAmount === 0
                ? t("Confirm")
                : t("Confirm & Refund to Wallet")}
            </button>
            <button
              className="back_btn mt-3"
              onClick={() => setCancelStep(1)}
              disabled={isLoading}
            >
              {t("No, Go Back")}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <SuccessIcon />
          <h2 className="fs_20 text-center mt-3 text_black">
            {t("Refund Successful!")}
          </h2>
          <p className="text_gray fs_14 text-center mt-3">
            {t(
              "The wallet has been credited, and the refund has been processed successfully"
            )}
          </p>
        </div>
      )}
    </>
  );
};

export default CancelOrder;

const CardDetailsCard = ({ orderDetails, t }) => {
  return (
    <div className="order_room_details">
      <div className="g_between gap-2 align-items-start">
        <h2 className="fs_16 text_black">
          {/* SHOW TYPE & NAME */}
          {
            orderSpaceType?.find(
              (option) => option.value === orderDetails?.spaceTypeInt
            )?.name
          }{" "}
          - {orderDetails?.spaceId?.name}, {/* SHOW QTY */}
          {orderDetails?.spaceTypeInt === 1
            ? orderDetails?.numberOfseats
            : [2, 3].includes(orderDetails?.spaceTypeInt)
            ? `${orderDetails?.durationInHour}h`
            : ""}
        </h2>
        <p
          className={`w_fit_content  ${
            orderStatusTypes?.find(
              (option) => option.value === orderDetails.bookingStatus
            )?.class
          }`}
        >
          {t(
            orderStatusTypes?.find(
              (option) => option.value === orderDetails.bookingStatus
            )?.name
          ) || "-"}
        </p>
      </div>
      <p className="fs_14 text_gray mt-1">
        {formatDate(orderDetails?.bookingDate)} - {orderDetails?.cityId?.name},{" "}
        {orderDetails?.branchId?.name}
      </p>
      <p className="fs_14 text_gray mt-1">
        {t("Customer")}: {orderDetails?.userId?.name}
      </p>
      <p className="fs_14 text_gray mt-1">
        <img src={Riyal} width={12} height={12} alt="riyal" className="me-1" />
        {orderDetails?.splitPayments?.total}
      </p>
    </div>
  );
};
